<template>
	<div>
		<v-alert :value="featureFlagsLoaded && !isFeatureEnabled" color="warning" icon="warning" class="text-xs-left mb-3" outline>
			Compliance reminders are not currently being sent out due to the feature being disabled. <router-link :to="{ name: 'features'}">Click here to change these settings.</router-link>
		</v-alert>
		<div class="report-toolbar mb-3">
			<v-layout row>
				<v-flex>
					<v-layout row wrap>
						<v-flex xl3 lg4 md4 sm6 xs12 class="pr-3">
							<v-text-field v-debounce:300ms="updateSearch" ref="focusAfterTagSelector" append-icon="search" label="Search Employees" autofocus="" background-color="#fff"></v-text-field>
						</v-flex>
						<v-flex xl3 lg4 md4 sm6 xs12 class="pr-3">
							<tag-selector
								:options="tagList"
								:selectedOptions="selectedTags"
								:isMatchAll="matchAllTags"
								placeholder="Enter Tag"
								@on-selected-tags-change="updateSelectedTags"
								@on-match-all-tags-change="updateMatchAllTags"
								@on-leave="onLeaveTagSelector"
							></tag-selector>
						</v-flex>
						<v-flex xl3 lg4 md4 sm6 xs12>
							<v-select v-model="filterDepartment" :items="departmentOptions" item-text="name" item-value="id" label="Department"></v-select>
						</v-flex>
					</v-layout>
				</v-flex>
			</v-layout>
		</div>
		<v-data-table :items="filteredReportData" :headers="headers" disable-initial-sort class="elevation-1" :loading="isLoading" :pagination.sync="pagination" :rows-per-page-items="rowsPerPageItems">
			<template slot="items" slot-scope="props">
				<tr>
					<td class="text-xs-left">
						<router-link :to="{ name: 'employee', params: { id: props.item.employee.id } }">{{ props.item.employee.firstName }} {{ props.item.employee.lastName }}</router-link>
					</td>
					<td class="text-xs-left">
						<AppDate :date="props.item.notificationSentDateUtc" :key="newGuid()" show-date format="Do MMM YYYY, HH:mm" />
					</td>
					<td class="text-xs-left">
						<ul class="my-2">
							<li v-if="props.item.unacknowledgedPD">
								<a href="#" @click.stop.prevent="onShowReminderDialog(props.item)">Unacknowledged position description</a>
							</li>
							<li v-if="props.item.unacknowledgedHrDocuments.length">
								<a href="#" @click.stop.prevent="onShowReminderDialog(props.item)">{{ props.item.unacknowledgedHrDocuments.length }} unacknowledged {{ props.item.unacknowledgedHrDocuments.length === 1 ? "policy" : "policies" }} </a>
							</li>
						</ul>
					</td>
                    <td style="width: 1px">
                        <v-icon title="Email" small>mail_outline</v-icon><span class="ml-1 caption" style="color: rgba(0, 0, 0, 0.54)">Email</span>
                    </td>
				</tr>
			</template>
		</v-data-table>
		<employee-compliance-reminder-dialog v-if="showReminderDialog" :dialog="showReminderDialog" :reminder="reminderDialogReminder" @onClose="onCloseReminderDialog" />
	</div>
</template>

<script>
    import { mapGetters, mapState } from 'vuex';
	import reportsApi from '@/api/reports.api';
	import moment from 'moment';
    import TagSelector from '@/components/tags/TagSelector';
	import EmployeeComplianceReminderDialog from '@/components/compliance/EmployeeComplianceReminderDialog';
	import FeatureFlag from '@/enums/featureFlag';

	export default {
        components: {
			TagSelector,
			EmployeeComplianceReminderDialog
		},
		data() {
			return {
				headers: [
					{ text: 'Employee Name', value: 'firstName', sortable: false},
					{ text: 'Date Sent', value: 'notificationSentDateUtc' },
					{ text: 'Reason(s)', value: 'reason', sortable: false },
                    { text: 'Delivery Method', value: 'deliveryMethod', sortable: false }
				],
				reportData: null,
				isLoading: false,
                filterDepartment: -1,
				search: null,
				selectedTags: [],
				matchAllTags: false,
                pagination: {
					rowsPerPage: 30,
				},
                rowsPerPageItems: [30, 50, 100, { text: '$vuetify.dataIterator.rowsPerPageAll', value: -1 }],
				showReminderDialog: false,
				reminderDialogReminder: null
			};
		},
		async created() {
			this.isLoading = true;

			await this.getReportData();

			this.isLoading = false;
		},
        computed: {
            ...mapGetters({
				tagList: 'tags/employeeTagList',
                departmentOptions: 'departments/departmentOptions',
                loggedInEmployee: 'loggedInEmployee',
			}),
			...mapState({
				featureFlagsLoaded: (state) => state.features.featureFlagsLoaded
			}),
			isFeatureEnabled() {
				return this.$store.getters['features/isFeatureEnabled'](FeatureFlag.EmployeeComplianceReminders);
			},
            employees() {
				return !this.reportData ? [] : [...new Set(this.reportData.map(x => x.employee))];
			},
            filteredReportData() {
                if (this.reportData == null) return [];
                return this.reportData.filter(x => this.filteredEmployeeIds.includes(x.employee.id))
            },
			filteredEmployeeIds() {
				const filterOptions = {
					search: this.search,
					tags: this.selectedTags,
					matchAllTags: this.matchAllTags,
					departmentId: this.filterDepartment !== -1 ? this.filterDepartment : null
				};
				return this.$_getFilteredEmployeeIds(this.loggedInEmployee, filterOptions);
			}
        },
		methods: {
			async getReportData() {
				try {
					const fromDate = moment.utc().subtract(6, 'months').toISOString();
					const data = await reportsApi.getEmployeeComplianceRemindersSentReport(fromDate);
					if (data) {
						this.reportData = data;
					}
				} catch (e) {}
			},
            updateSelectedTags(newSelectedTags) {
				this.selectedTags = newSelectedTags;
			},
			updateMatchAllTags(newMatchAll) {
				this.matchAllTags = newMatchAll;
			},
			onLeaveTagSelector() {
				this.$refs.focusAfterTagSelector.focus();
			},
			updateSearch(val) {
				this.search = val;
			},
			onShowReminderDialog(reminder) {
				this.reminderDialogReminder = reminder;
				this.showReminderDialog = true;
			},
			onCloseReminderDialog() {
				this.reminderDialogReminder = null;
				this.showReminderDialog = false;
			}
		}
	};
</script>